import { z } from 'zod';
import { optionalE164PhoneSchema } from './common/phone.js';

const objectId = z.string().regex(/^[0-9a-fA-F]{24}$/);

const userStatusValues = z.enum([
    'active',
    'inactive',
    'pending',
    'suspended',
    'Active',
    'Not Active',
    'Suspended',
]);

const notificationsShape = z.object({
    email: z.boolean().optional(),
    inApp: z.boolean().optional(),
    sms: z.boolean().optional(),
}).optional();

const preferencesShape = z.object({
    language: z.string().optional(),
    region: z.string().optional(),
    documentClassificationDegree: z.string().optional(),
    notifications: notificationsShape,
}).optional();

function mergeRoleIdFields(data) {
    if (Array.isArray(data.roleIds) && data.roleIds.length > 0) {
        return [...new Set(data.roleIds)];
    }
    if (data.roleId) {
        return [data.roleId];
    }
    return [];
}

function refineAtLeastOneRole(data, ctx) {
    if (mergeRoleIdFields(data).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'En az bir rol seçilmelidir',
            path: ['roleIds'],
        });
    }
}

function transformStripRoleAliases(data) {
    const roleIds = mergeRoleIdFields(data);
    const { roleId: _rid, roleIds: _rids, ...rest } = data;
    return { ...rest, roleIds };
}

const roleFields = {
    roleId: objectId.optional(),
    roleIds: z.array(objectId).optional(),
};

/**
 * Unit-role assignment for the new unit-role model.
 * Each entry binds a (unit, role) pair to this user.
 * The API rejects entries whose (unit, role) is already
 * owned by another user unless `forceReplace` is set.
 */
const unitRoleAssignmentFields = {
    unitRoleAssignments: z.array(z.object({
        unit: objectId,
        role: objectId,
    })).optional(),
    forceReplaceUnitRoles: z.boolean().optional(),
};

const userManagementApiCreateFields = z.object({
    email: z.string().email(),
    password: z.string().min(6).optional().or(z.literal('')),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    userName: z.string().min(3).optional(),
    title: z.string().optional(),
    phoneNumber: optionalE164PhoneSchema,
    status: z.enum(['active', 'inactive', 'pending', 'suspended']).optional(),
    userStatus: userStatusValues.optional(),
    ...roleFields,
    ...unitRoleAssignmentFields,
    unitId: z.union([objectId, z.literal('')]).optional().nullable(),
    region: z.string().optional(),
    preferences: preferencesShape,
    language: z.string().optional(),
    mfaEnabled: z.boolean().optional(),
    notifications: notificationsShape,
    documentClassificationDegree: z.string().optional(),
    /** Tenant membership MAC clearance (Classification.level ladder). */
    clearanceLevel: z.number().int().min(0).max(99).optional(),
    standardSelection: z.string().optional(),
    passwordExpirationPeriod: z.string().optional(),
});

/**
 * Body validation for POST /user (API + shared with SDK outbound payload after parse).
 */
export const userManagementApiCreateSchema = userManagementApiCreateFields
    .superRefine(refineAtLeastOneRole)
    .transform(transformStripRoleAliases);

const objectIdInvite = z.string().regex(/^[0-9a-fA-F]{24}$/);

/** POST /user/invite — no password; sends activation / returns link for admin copy */
export const InviteUserSchema = z.object({
    email: z.string().email(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    roleIds: z.array(objectIdInvite).min(1),
    unitId: z.union([objectIdInvite, z.literal('')]).optional().nullable(),
    title: z.string().optional()
});

/** POST /user/batch-patch — toplu durum ve/veya kiracı üyeliğine rol ekleme */
export const BatchPatchUsersSchema = z.object({
    userIds: z.array(objectIdInvite).min(1).max(200),
    status: z.enum(['active', 'inactive', 'pending', 'suspended']).optional(),
    appendRoleIds: z.array(objectIdInvite).optional()
}).superRefine((data, ctx) => {
    const hasRoles = Array.isArray(data.appendRoleIds) && data.appendRoleIds.length > 0;
    if (!data.status && !hasRoles) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'En azından status veya appendRoleIds belirtilmelidir',
            path: ['status']
        });
    }
});

const userManagementApiUpdateFields = z.object({
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    userName: z.string().min(3).optional(),
    email: z.string().email().optional(),
    title: z.string().optional(),
    phoneNumber: optionalE164PhoneSchema,
    status: z.enum(['active', 'inactive', 'pending', 'suspended']).optional(),
    userStatus: userStatusValues.optional(),
    ...roleFields,
    ...unitRoleAssignmentFields,
    unitId: z.union([objectId, z.literal('')]).optional().nullable(),
    region: z.string().optional(),
    language: z.string().optional(),
    mfaEnabled: z.boolean().optional(),
    notifications: notificationsShape,
    documentClassificationDegree: z.string().optional(),
    /** Tenant membership MAC clearance (Classification.level ladder). */
    clearanceLevel: z.number().int().min(0).max(99).optional(),
    standardSelection: z.string().optional(),
    passwordExpirationPeriod: z.string().optional(),
});

function refineUpdateRoles(data, ctx) {
    const hasRoleIdsKey = Object.prototype.hasOwnProperty.call(data, 'roleIds');
    const hasRoleIdKey = Object.prototype.hasOwnProperty.call(data, 'roleId');
    if (!hasRoleIdsKey && !hasRoleIdKey) return;
    if (mergeRoleIdFields(data).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'En az bir rol seçilmelidir',
            path: ['roleIds'],
        });
    }
}

function transformUpdateRoles(data) {
    const hasRoleIdsKey = Object.prototype.hasOwnProperty.call(data, 'roleIds');
    const hasRoleIdKey = Object.prototype.hasOwnProperty.call(data, 'roleId');
    if (!hasRoleIdsKey && !hasRoleIdKey) {
        return data;
    }
    return transformStripRoleAliases(data);
}

/**
 * Body validation for PUT /user/:id
 */
export const userManagementApiUpdateSchema = userManagementApiUpdateFields
    .superRefine(refineUpdateRoles)
    .transform(transformUpdateRoles);

/**
 * SDK: user-management create — keeps password + userName; validates confirmPassword then strips it.
 */
export const UserManagementCreateSchema = userManagementApiCreateFields
    .extend({
        confirmPassword: z.string().optional().or(z.literal('')),
    })
    .superRefine(refineAtLeastOneRole)
    .superRefine((data, ctx) => {
        if (!data.password || data.password.length < 6) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Şifre en az 6 karakter olmalıdır',
                path: ['password'],
            });
        }
        if (data.password !== data.confirmPassword) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Şifreler eşleşmiyor',
                path: ['confirmPassword'],
            });
        }
    })
    .transform(({ confirmPassword: _confirmPassword, ...rest }) => transformStripRoleAliases(rest));

/**
 * SDK: user-management update — accepts optional password fields from form, validates if set, strips (API uses change-password).
 */
export const UserManagementUpdateSchema = userManagementApiUpdateFields
    .extend({
        password: z.string().optional().or(z.literal('')),
        confirmPassword: z.string().optional().or(z.literal('')),
    })
    .superRefine((data, ctx) => {
        const pwd = data.password;
        if (pwd && pwd.length > 0) {
            if (pwd.length < 6) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Şifre en az 6 karakter olmalıdır',
                    path: ['password'],
                });
            }
            if (pwd !== data.confirmPassword) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Şifreler eşleşmiyor',
                    path: ['confirmPassword'],
                });
            }
        }
    })
    .superRefine(refineUpdateRoles)
    .transform(({ password: _p, confirmPassword: _c, ...rest }) => {
        const hasRoleIdsKey = Object.prototype.hasOwnProperty.call(rest, 'roleIds');
        const hasRoleIdKey = Object.prototype.hasOwnProperty.call(rest, 'roleId');
        if (!hasRoleIdsKey && !hasRoleIdKey) {
            return rest;
        }
        return transformStripRoleAliases(rest);
    });
